import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LanguageCode } from '@shared/constants/language';
import { AppInfo } from '@shared/models/AppInfo.model';
import { PartyInfoDTO } from '@shared/models/PartyInfoDTO.model';
import { LanguagePreference, UserPreferencesDTO } from '@shared/models/UserPreferencesDTO.model';

interface AppState {
  user: PartyInfoDTO | null;
  userPreferences: UserPreferencesDTO;
  inboxStatus: { hasUnread: boolean };
  pushNotifications: {
    topics: {
      hasPensions: boolean;
      hasContributions: boolean;
      hasDebts: boolean;
    };
  };
  info: AppInfo;
}

const initialState: AppState = {
  user: null,
  userPreferences: {
    isDigitalInsuranceHistoryActive: false,
    language: { name: 'Greek', value: LanguageCode.GR }
  },
  inboxStatus: { hasUnread: false },
  pushNotifications: {
    topics: {
      hasPensions: false,
      hasContributions: false,
      hasDebts: false
    }
  },
  info: {
    version: '',
    mode: '',
    buildDate: null,
    isDevelopment: false,
    isProduction: false,
    mobileVersion: '',
    isMobileVersionCompatible: true,
    isThereANewerMobileVersionAvailable: false,
    storeUrls: {
      ios: 'https://apps.apple.com/gr/app/myefka/id6749564966',
      android: 'https://play.google.com/store/apps/details?id=efka.dashboard'
    }
  }
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<{ user: PartyInfoDTO }>) => {
      state.user = action.payload.user;
    },
    setUserPreferences: (state, action: PayloadAction<UserPreferencesDTO>) => {
      state.userPreferences = action.payload;
    },
    setUserPreferencesLanguage: (state, action: PayloadAction<LanguagePreference>) => {
      if (state.userPreferences.language.value === action.payload.value) return;
      state.userPreferences.language = action.payload;
    },
    setInboxStatus: (state, action: PayloadAction<{ hasUnread: boolean }>) => {
      state.inboxStatus.hasUnread = action.payload.hasUnread;
    },
    setPushNotificationsTopics: (
      state,
      action: PayloadAction<Partial<{ hasPensions: boolean; hasContributions: boolean; hasDebts: boolean }>>
    ) => {
      state.pushNotifications.topics = {
        ...state.pushNotifications.topics,
        ...action.payload
      };
    },
    setAppInfo: (state, action: PayloadAction<AppInfo>) => {
      state.info = action.payload;
    }
  }
});

export const AppActions = appSlice.actions;
export const AppReducer = appSlice.reducer;
